import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { configData } from '../config';
import { VISUAL_PAGE_TYPES, isValidVisualPageType } from '../constants/visualPageTypes';
import { makeCourseTemplate } from './PageEditor/templates/courseTemplate';

// Create-a-Visual-Page form.
//
// Flow: admin picks a Page type (Course or Conference), then picks an
// EXISTING course/conference page from the CMS (fetched via the same
// `coursepages/managecoursepages` endpoint the Manage screens already use).
// Clicking Create builds a visual tree and POSTs it to
// `pages/createvisualpage`. A brand-new visual_pages row is created; the
// source Drupal page is not touched.
//
// How the tree is built depends on the page type:
//
//   COURSE — always the fixed template in
//     PageEditor/templates/courseTemplate.js. The selected course does NOT
//     supply layout, CSS, HTML or section structure; two pages created from
//     two different courses are identical apart from the sourceNid /
//     sourcePath recorded on the root node. Because nothing is read from the
//     source page, `pages/getpagedata` is not called at all on this path, so
//     course creation is immediate. The nid comes from the list row's
//     edit_url, and both it and the path exist purely so dynamic components
//     dropped later arrive pre-bound to that course.
//
//   CONFERENCE — unchanged: buildTreeFromSource below still mirrors how
//     Page.js renders the picked source page.
//
// The list endpoint only returns { title, edit_url, view_url }; nid is
// parsed out of edit_url (e.g. "node/505/edit" → 505) so dynamic-mentors,
// dynamic-success-stories and dynamic-testimonials can seed `sourceNid`
// even if the getpagedata call is slow or fails.

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 128);

const uid = () => Math.random().toString(36).slice(2, 10);

// Strip origin + leading/trailing slashes so slugs land as
// "courses/athlete-management-sports-agent" — the shape `getpagedata`
// expects on `?path=`.
const pathFromUrl = (u) => {
  if (!u) return '';
  const stripped = String(u).replace(/^https?:\/\/[^/]+/, '');
  return stripped.replace(/^\/+/, '').replace(/\/+$/, '');
};

// Filter manage-endpoint rows to the picked type. The endpoint doesn't
// return a `type` column; view_url prefix (/courses/, /conferences/) is
// the only signal available.
const rowMatchesType = (row, type) => {
  if (row && row.type) return String(row.type) === type;
  const path = pathFromUrl(row?.view_url || row?.edit_url || '');
  if (type === 'course')     return /^courses(\/|$)/.test(path);
  if (type === 'conference') return /^conferences(\/|$)/.test(path);
  return false;
};

// edit_url looks like "node/505/edit" or "/node/505/edit". Pull the nid
// out for dynamic-component seeding.
const nidFromEditUrl = (u) => {
  if (!u) return null;
  const m = String(u).match(/node[/](\d+)/);
  return m ? Number(m[1]) : null;
};

// Fetch pages/getpagedata with a bounded timeout. Returns null on any
// failure (network, timeout, non-ok status, unparseable body) so the
// caller can still build a tree using just the nid + title from the
// list-endpoint row.
async function fetchSourceWithTimeout(app_url, path, timeoutMs) {
  const controller = new AbortController();
  const to = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const r = await fetch(app_url + 'pages/getpagedata?path=' + encodeURIComponent(path), {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
    });
    clearTimeout(to);
    if (!r.ok) return null;
    const j = await r.json().catch(() => null);
    return (j && j.data) || null;
  } catch (e) {
    clearTimeout(to);
    return null;
  }
}

// Build a visual tree that mirrors the way Page.js renders the source page.
//
// CONFERENCE ONLY. Course pages no longer come through here — they get the
// fixed template from makeCourseTemplate() instead, precisely so that the
// source's HTML body and block list can't shape the new page. The course
// branches below are left intact rather than deleted: they are still the
// reference for what "mirror the source" means, and removing them would be
// a behaviour change for the conference path that shares this code.
//
// Dynamic components (mentors, success stories,
// testimonials, quick form) fetch live from the same Yii2 endpoints
// they always have, so their content is authored on the source page and
// stays in sync automatically. HTML body + each course block are
// preserved verbatim as html / card nodes so the specific page's exact
// content, order, and images come through in the editor.
function buildTreeFromSource({ nid, title, pageType, source, sourcePath }) {
  const node = (type, props, children) => {
    const n = { id: uid(), type, props: props || {} };
    if (children !== undefined) n.children = children;
    return n;
  };

  const isCourse     = pageType === 'course';
  const isConference = pageType === 'conference';

  // Robust field extraction — accept a few possible shapes. `body` is only a
  // fallback now; with a sourcePath the centre renders live via
  // dynamic-course-overview rather than embedding a copy of the HTML.
  const body           = String((source && (source.body || source.content)) || '');
  const blocks         = Array.isArray(source && source.blocks) ? source.blocks : [];

  const path = String(sourcePath || '');

  // ---- Left sidebar (mirrors Page.js: <Mentor> + <SidebarQuickForm>).
  const left = [];
  if (nid) left.push(node('dynamic-mentors', { sourceNid: nid }));
  left.push(node('dynamic-quick-form', path ? { sourcePath: path } : {}));

  // ---- Right sidebar (mirrors Page.js: <SuccessStories> + <Testimonials>).
  const right = [];
  if (isCourse) {
    if (nid) right.push(node('dynamic-success-stories', { sourceNid: nid }));
    if (nid) right.push(node('dynamic-testimonials',    { sourceNid: nid }));
  } else if (isConference) {
    // Conferences don't have a defined right-column render in Page.js;
    // include mentors so the sidebar isn't empty and the palette rule
    // (which excludes stories/testimonials for conferences anyway) is
    // respected.
    if (nid) right.push(node('dynamic-mentors', { sourceNid: nid }));
  }

  // ---- Center column. Mirrors Page.js exactly, verified against the live
  //      course page's DOM: the center holds <Body> (.courses-overview-
  //      container) followed by each block rendered via <BlockRenderer>.
  //      Page.js renders NO title element — the title goes only to <Helmet> —
  //      so none is emitted here.
  const center = [];
  if (path) {
    // Course Overview — a live component bound to the source page, rendered by
    // the same <Body> Page.js uses. Not a frozen copy of the HTML, so the
    // body's shortcodes (currency-converter, mobile-quick-form,
    // mobile-mentor-block) still mount as real React on the visual page.
    center.push(node('dynamic-course-overview', { sourcePath: path }));
  } else if (body.trim()) {
    // No source path (shouldn't happen via the Create form) — fall back to a
    // verbatim Raw HTML node so content is never silently lost.
    center.push(node('html', { html: body }));
  }
  // Course blocks, in the order `blocks[]` returns them — reproducing what
  // BlockRenderer.js actually emits, nothing more. Verified against the live
  // page: what_you_learn renders "<h2>What You'll Learn</h2><div>…</div>",
  // alumni renders "<h2>Alumni</h2>" alone, and course_cta renders an empty
  // div. Nothing is invented here — no card chrome, no alumni story list and
  // no CTA button, because the source page has none of those.
  const BLOCK_NODE_TYPE = {
    what_you_learn: 'dynamic-what-you-learn',
    alumni:         'dynamic-alumni',
    course_cta:     'dynamic-course-cta',
  };
  for (const block of blocks) {
    if (!block || !block.block_type) continue;
    const nodeType = BLOCK_NODE_TYPE[String(block.block_type)];
    if (!nodeType) continue;
    center.push(node(nodeType, path ? { sourcePath: path } : {}));
  }

  // If getpagedata timed out or returned an empty body AND there are no
  // blocks, the center column would be empty. Add an editable placeholder so
  // the canvas is never blank and the admin has an obvious drop target.
  if (center.length === 0) {
    center.push(node('text', {
      text: 'Source body was not fetched — add the page content here, or drop dynamic components from the palette.',
      size: 14, color: '#6b7280', marginTop: 8,
    }));
  }

  // ---- Assemble. Course = three-column layout reproducing the real page.
  //      Conference = single-column (Page.js has no specific conf render).
  //
  // Column weights and gap are measured from the live course page rather than
  // guessed. At a 1170px container, #main.three-column-layout resolves to:
  //   .left 195px (margin 0 23.4 0 11.7) | .center 631.8px | .right 284.7px
  //   11.7 + 195 + 23.4 + 631.8 + 23.4 + 284.7 = 1170
  // T.column renders `flex: <weight>` with flex-basis 0, so using the measured
  // pixel widths as weights reproduces those proportions exactly. The 11.7px
  // outer inset becomes the section's left padding; the two 23.4px gutters
  // become the row gap.
  const mainSection = isCourse
    ? node('section',
        { background: '#ffffff',
          padding: { t: 0, r: 0, b: 40, l: 12 },
          maxWidth: 1170, textAlign: 'left' },
        [
          node('row', { gap: 23, align: 'flex-start' }, [
            node('column', { weight: 195 }, left),
            node('column', { weight: 632 }, center),
            node('column', { weight: 285 }, right),
          ]),
        ])
    : node('section',
        { background: '#ffffff',
          padding: { t: 24, r: 20, b: 40, l: 20 },
          maxWidth: 1170, textAlign: 'left' },
        center);

  // The source nid is recorded once on the root node's `props` (previously an
  // unused empty object) so the editor can bind newly dropped dynamic
  // components to this page's course without the admin typing an nid. It rides
  // along inside the existing tree envelope — no new node type, no schema
  // change, no extra column, and save/load round-trip it untouched.
  const rootProps = {};
  if (nid) rootProps.sourceNid = nid;
  if (path) rootProps.sourcePath = path;
  // The source's block_type list drives the Dynamic Components panel so it
  // offers the components applicable to THIS course rather than every block a
  // course could theoretically have.
  const blockTypes = blocks.map((b) => b && b.block_type).filter(Boolean).map(String);
  if (blockTypes.length) rootProps.sourceBlocks = blockTypes;

  return { id: uid(), type: 'root', props: rootProps, children: [mainSection] };
}

const AddVisualPage = () => {
  const app_url = configData.APP_URL;
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [urlSlug, setUrlSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [pageType, setPageType] = useState('');
  const [sourceRows, setSourceRows] = useState([]);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceError, setSourceError] = useState('');
  const [selectedSourcePath, setSelectedSourcePath] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setSelectedSourcePath('');
    setSourceRows([]);
    setSourceError('');
    if (!pageType) return;
    let cancelled = false;
    setSourceLoading(true);
    (async () => {
      try {
        const res = await fetch(app_url + 'coursepages/managecoursepages', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: 0, pageSize: 500,
            sortModel: [], filterModel: { items: [] },
            formData: { title: '' },
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || (data && data.code && data.code !== 0)) {
          throw new Error((data && data.message) || ('HTTP ' + res.status));
        }
        const rows = (data && data.data && data.data.rows) || [];
        setSourceRows(rows.filter((r) => rowMatchesType(r, pageType)));
      } catch (err) {
        if (cancelled) return;
        setSourceError('Could not load existing pages: ' + (err.message || 'unknown error'));
      } finally {
        if (!cancelled) setSourceLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pageType, app_url]);

  const onTitleChange = (e) => {
    const v = e.target.value;
    setTitle(v);
    if (!slugTouched) setUrlSlug(slugify(v));
  };

  const onSourceChange = (e) => {
    const path = e.target.value;
    setSelectedSourcePath(path);
    if (!path) return;
    const row = sourceRows.find((r) => pathFromUrl(r.view_url || r.edit_url) === path);
    if (!row) return;
    setTitle(row.title || '');
    setUrlSlug(slugify((row.title || 'page') + '-visual'));
    setSlugTouched(false);
  };

  const validate = () => {
    const errs = {};
    if (!pageType) errs.page_type = 'Page type is required.';
    else if (!isValidVisualPageType(pageType)) errs.page_type = 'Choose a valid page type.';
    if (!title.trim()) errs.title = 'Title is required.';
    if (!urlSlug.trim()) errs.url_slug = 'URL slug is required.';
    else if (!/^[a-z0-9-/]+$/.test(urlSlug)) errs.url_slug = 'Only lowercase letters, digits, "-" and "/" are allowed.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreate = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setErrorMessage('');
    setProgressMsg('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      let bodyEnvelope = '';
      // Nid is available immediately from the list row (parsed from
      // edit_url) whenever a source was picked. Dynamic components need it.
      const row = selectedSourcePath
        ? sourceRows.find((r) => pathFromUrl(r.view_url || r.edit_url) === selectedSourcePath)
        : null;
      const nid = nidFromEditUrl(row && row.edit_url);

      if (pageType === 'course') {
        // Fixed template, always. No getpagedata call, no reading of the
        // source's body or blocks — so the layout cannot vary by course, and
        // creation is immediate. nid/sourcePath ride along on the root node
        // only, to pre-bind dynamic components dropped later. A course page
        // created without picking a source gets the identical template, just
        // without those bindings.
        const tree = makeCourseTemplate({ nid, sourcePath: selectedSourcePath });
        bodyEnvelope = JSON.stringify({ version: 2, tree });
      } else if (selectedSourcePath) {
        setProgressMsg('Loading source page…');
        // getpagedata can be slow; 20s is generous and still bounded so
        // an unresponsive backend can't hang the create flow forever.
        const source = await fetchSourceWithTimeout(app_url, selectedSourcePath, 20000);
        const tree = buildTreeFromSource({
          nid,
          title: row && row.title,
          pageType,
          source,
          sourcePath: selectedSourcePath,
        });
        bodyEnvelope = JSON.stringify({ version: 2, tree });
      }
      setProgressMsg('Creating visual page…');
      const payload = {
        title: title.trim(),
        url_slug: urlSlug.trim(),
        page_type: pageType,
      };
      if (bodyEnvelope) payload.body = bodyEnvelope;
      const res = await fetch(app_url + 'pages/createvisualpage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Backend not available (HTTP ' + res.status + ')');
      const data = await res.json().catch(() => ({}));
      if (data && data.code && data.code !== 0) {
        throw new Error((data && data.message) || 'Create failed');
      }
      const createdSlug = (data && data.data && data.data.url_slug) || urlSlug.trim();
      const msg = pageType === 'course'
        ? 'Visual page created from the standard Course template. Source untouched.'
        : selectedSourcePath
        ? 'Visual page created from existing page. Source untouched.'
        : 'Visual page created.';
      navigate('/' + createdSlug + '/edit', { state: { message: msg } });
    } catch (err) {
      setErrorMessage(
        'Create failed: ' + (err && err.message ? err.message : 'unknown error')
      );
    } finally {
      setSubmitting(false);
      setProgressMsg('');
    }
  };

  const selectedTypeLabel = VISUAL_PAGE_TYPES.find((t) => t.value === pageType)?.label || pageType;

  return (
    <div id="main">
      <div className="left"></div>
      <div className="center left-center">
        <Helmet>
          <title>Create Visual Page</title>
        </Helmet>
        <div style={{ maxWidth: 640, padding: '12px 0' }}>
          <h2 style={{ fontSize: 20, margin: '0 0 12px' }}>Create Visual Page</h2>
          {errorMessage && (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                color: '#b91c1c',
                padding: '10px 12px',
                borderRadius: 6,
                marginBottom: 12,
                fontSize: 13,
              }}
            >
              {errorMessage}
            </div>
          )}
          <form onSubmit={handleCreate}>
            <div style={{ marginBottom: 12 }}>
              <label htmlFor="visual-page-type" style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
                Page type <span style={{ color: '#b91c1c' }}>*</span>
              </label>
              <select
                id="visual-page-type"
                name="page_type"
                value={pageType}
                onChange={(e) => setPageType(e.target.value)}
                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 5, background: '#fff' }}
              >
                <option value="">— Select page type —</option>
                {VISUAL_PAGE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                The content type this page represents. Stored alongside the page; the internal storage type stays <code>visual</code>.
              </div>
              {errors.page_type && (
                <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 4 }}>{errors.page_type}</div>
              )}
            </div>

            {pageType && (
              <div
                style={{
                  marginBottom: 12,
                  padding: '10px 12px',
                  border: '1px solid #e4e6eb',
                  borderRadius: 6,
                  background: '#f9fafb',
                }}
              >
                <label htmlFor="visual-source-path" style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
                  {pageType === 'course'
                    ? 'Link this page to an existing Course (optional)'
                    : 'Start from an existing ' + selectedTypeLabel + ' page (optional)'}
                </label>
                <select
                  id="visual-source-path"
                  name="source_path"
                  value={selectedSourcePath}
                  onChange={onSourceChange}
                  disabled={sourceLoading}
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 5, background: '#fff' }}
                >
                  <option value="">
                    {sourceLoading
                      ? 'Loading existing pages…'
                      : pageType === 'course'
                      ? (sourceRows.length === 0
                          ? '— No existing courses — template only —'
                          : '— No course link — template only —')
                      : sourceRows.length === 0
                      ? '— No existing pages of this type — start blank —'
                      : '— Start blank —'}
                  </option>
                  {sourceRows.map((row, i) => {
                    const path = pathFromUrl(row.view_url || row.edit_url);
                    return (
                      <option key={row.nid || row.id || row.edit_url || i} value={path}>
                        {row.title} (/{path})
                      </option>
                    );
                  })}
                </select>
                {sourceError && (
                  <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 4 }}>{sourceError}</div>
                )}
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {pageType === 'course' ? (
                    <>
                      Every Course visual page starts from the same standard
                      Course template — the layout, styling and sections do not
                      change with the course you pick here. The course is used
                      only as the data source for dynamic components you add
                      later; picking one now pre-fills them for you. The source
                      page is not modified.
                    </>
                  ) : (
                    <>
                      Loads the picked page's layout, body content, images,
                      blocks, mentors, testimonials and success stories into a
                      brand-new visual page. The source page is not modified.
                    </>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <label htmlFor="visual-title" style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
                Title <span style={{ color: '#b91c1c' }}>*</span>
              </label>
              <input
                type="text"
                id="visual-title"
                name="title"
                value={title}
                onChange={onTitleChange}
                maxLength={255}
                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 5 }}
              />
              {errors.title && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 4 }}>{errors.title}</div>}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label htmlFor="visual-url-slug" style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
                URL slug <span style={{ color: '#b91c1c' }}>*</span>
              </label>
              <input
                type="text"
                id="visual-url-slug"
                name="url_slug"
                value={urlSlug}
                onChange={(e) => {
                  setUrlSlug(e.target.value);
                  setSlugTouched(true);
                }}
                onBlur={(e) => setUrlSlug(slugify(e.target.value))}
                maxLength={255}
                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 5 }}
              />
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Page will live at <code>/{urlSlug || 'your-slug'}</code>. Cannot collide with the source page's own URL.
              </div>
              {errors.url_slug && (
                <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 4 }}>{errors.url_slug}</div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="submit"
                className="form-submit"
                disabled={submitting}
                style={{
                  background: '#2f81f7',
                  color: '#fff',
                  border: 0,
                  padding: '8px 14px',
                  borderRadius: 5,
                  cursor: submitting ? 'default' : 'pointer',
                }}
              >
                {submitting ? 'Creating…' : 'Create'}
              </button>
              <a
                href="/manage-visual-pages"
                style={{ padding: '8px 14px', color: '#4b5563', textDecoration: 'none' }}
              >
                Cancel
              </a>
              {progressMsg && (
                <span style={{ color: '#6b7280', fontSize: 12 }}>{progressMsg}</span>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddVisualPage;
